import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App, { preloadRoute } from "./App.tsx";
import { initClarity } from "./services/clarity";
import { initGoogleTagManager } from "./services/gtm";
import { initGtag } from "./services/gtag";
import { getStoredConsent } from "./services/cookieConsent";
// Inter autohospedada (fuente variable, un solo archivo cubre los pesos
// 300-900). Antes se traia desde Google Fonts: ese request a un origen
// externo tiene un cache-lifetime corto -fuera de nuestro control- que
// Lighthouse marca en "Usa tiempos de almacenamiento en caché eficientes".
// Autohospedada la sirve el propio Nginx con el `expires 1y` de nginx.conf.
import "@fontsource-variable/inter";
import "./index.css";

// Clarity no arranca solo: solo si el visitante ya acepto cookies en una
// visita anterior. La primera vez, quien lo dispara es CookieConsent al
// aceptar (ver src/components/CookieConsent/CookieConsent.tsx).
if (getStoredConsent() === "accepted") {
  initClarity();
}

// gtag (Analytics 4) y GTM si arrancan siempre: el modo de consentimiento
// avanzado necesita que las etiquetas esten presentes desde el arranque, con
// el consentimiento por defecto ya seteado adentro de initGtag() (mismo
// dataLayer que lee GTM). Lo que se difiere es la descarga de los dos scripts
// de Google, no el consentimiento: eso se encola en el dataLayer ahora mismo.
initGtag();
initGoogleTagManager();

const rootEl = document.getElementById("root")!;

const app = (
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// Una pagina bajo demanda puede no llegar si el chunk ya no existe (se publico
// una version nueva mientras esta pestaña seguia abierta) o se corto la red. Se
// recarga una vez para traer el HTML y los archivos nuevos; el tope de tiempo
// evita un bucle si el problema es permanente.
window.addEventListener("vite:preloadError", () => {
  try {
    const last = Number(sessionStorage.getItem("waia:chunk-reload"));
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem("waia:chunk-reload", String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
});

// Las paginas se cargan bajo demanda: la de esta URL (y el cuerpo de la nota,
// si es una) tiene que estar lista ANTES de renderizar. Si no, la hidratacion
// encontraria la pagina sin cargar y la reemplazaria por un fallback vacio en
// vez de enganchar el HTML pre-renderizado.
let routeReady = true;
try {
  await preloadRoute(window.location.pathname);
} catch {
  // El chunk de la pagina no llego. Sin hidratar, el HTML pre-renderizado queda
  // legible y sus links siguen funcionando (son cargas de pagina completas);
  // hidratar igual lo reemplazaria por un error en vez de dejarlo a la vista.
  routeReady = false;
}

// En desarrollo el #root está vacío (Vite no pre-renderiza) -> createRoot.
// En producción el HTML ya viene pre-renderizado -> hydrateRoot (lo "engancha").
if (routeReady) {
  if (import.meta.env.DEV) {
    createRoot(rootEl).render(app);
  } else {
    hydrateRoot(rootEl, app);
  }
}
